<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70">
    <div class="w-full max-w-4xl m-3 overflow-hidden transition-all duration-200 bg-gray-800 rounded-lg shadow-xl border border-gray-700 flex flex-col" style="max-height:60vh;">
      <!-- Header -->
      <header class="bg-gray-800 border-b border-gray-700">
        <div class="flex items-center justify-between p-4">
          <div class="flex items-center gap-3">
            <i class="fa-solid fa-users text-indigo-300 text-xl"></i>
            <div>
              <h2 class="text-base sm:text-lg font-semibold text-indigo-300">{{ gangData.name }}</h2>
            </div>
          </div>
          <div class="flex items-center gap-2">
            <button @click="$emit('cancelModal')" class="text-gray-300 hover:text-indigo-300 transition-colors duration-200 group focus:outline-none p-2" aria-label="ปิด">
              <i class="fas fa-times text-lg group-hover:scale-110 transition-transform duration-200"></i>
            </button>
          </div>
        </div>
        <!-- Primary Tabs -->
        <nav v-if="!isSelectingVillagers" class="grid grid-cols-3 bg-gray-700 bg-opacity-60">
          <button
            v-for="tab in filteredTabs"
            :key="tab.id"
            @click="activeTab = tab.id"
            class="relative px-3 sm:px-4 py-2 text-xs sm:text-sm font-medium transition-colors duration-200 flex items-center justify-center gap-2 border-b-2"
            :class="activeTab === tab.id ? 'text-indigo-300 border-indigo-600' : 'text-gray-300 border-transparent hover:bg-gray-700 hover:text-indigo-200'"
          >
            <i :class="tab.icon"></i>
            <span>{{ tab.name }}</span>
            <span v-if="activeTab === tab.id" class="absolute bottom-0 left-0 w-full h-0.5 bg-indigo-600 animate-tabSlide"></span>
          </button>
        </nav>
      </header>

      <!-- Content -->
      <main class="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin">
        <template v-if="!isSelectingVillagers">
          <!-- INFO TAB -->
          <section v-if="activeTab === 'info'" class="space-y-4 animate-fadeIn">
            <!-- Image -->
            <div class="flex items-center justify-center">
              <img
                v-if="gangData.img && !imgBroken"
                :src="gangData.img"
                class="w-72 rounded  "
                @error="imgBroken = true"
              />
              <div v-else class="text-sm text-gray-400">ไม่มีรูปภาพ</div>
            </div>

            <!-- Quick facts -->
            <div class="rounded-lg border border-gray-700 bg-gray-800 bg-opacity-70 px-3 py-3 sm:px-4 sm:py-4">
              <div class="grid grid-cols-2 sm:grid-cols-3 gap-2 sm:gap-3 text-xs text-gray-300">
                <div class="rounded-md border border-gray-700 bg-gray-800 bg-opacity-60 px-3 py-2 hover:bg-gray-700 transition-colors">
                  <div class="flex items-center space-x-1 text-gray-400 text-xxs">
                    <i class="fa-solid fa-map-marker-alt text-indigo-300"></i>
                    <span>สถานที่</span>
                  </div>
                  <div class="mt-0.5 text-sm font-semibold text-gray-200">
                    {{ gangData.location || 'ไม่ระบุ' }}
                  </div>
                </div>
                <div class="rounded-md border border-gray-700 bg-gray-800 bg-opacity-60 px-3 py-2 hover:bg-gray-700 transition-colors">
                  <div class="flex items-center space-x-1 text-gray-400 text-xxs">
                    <i class="fa-solid fa-users text-indigo-300"></i>
                    <span>ชาวบ้านขั้นต่ำเพื่อบุก</span>
                  </div>
                  <div class="mt-0.5 text-sm font-semibold text-gray-200">
                    {{ gangData.minVillagersToAttack || 0 }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Prerequisites -->
            <div class="bg-gray-800 bg-opacity-70 border border-gray-700 rounded-md p-3">
              <div class="text-xs text-gray-400 mb-1">แก๊งค์ที่ต้องชนะก่อน</div>
              <div class="flex flex-wrap gap-2">
                <span v-for="prereq in gangData.prerequisites || []" :key="prereq"
                  class="px-2 py-1 text-xs rounded-full bg-indigo-700 border border-indigo-500 text-indigo-100">{{ prereq }}</span>
                <span v-if="!(gangData.prerequisites && gangData.prerequisites.length)" class="text-xs text-indigo-300">— ไม่มีข้อมูล —</span>
              </div>
            </div>
          </section>

          <!-- MEMBERS TAB -->
          <section v-if="activeTab === 'members'" class="space-y-4 animate-fadeIn">
            <div class="bg-gray-900 bg-opacity-60 border border-gray-700 rounded-md p-3">
              <div class="flex items-center justify-between mb-2">
                <h3 class="text-xs font-semibold text-indigo-300">สมาชิก</h3>
                <div class="text-xxs text-gray-400">{{ membersData.length }} คน</div>
              </div>
              <div v-if="loadingMembers" class="flex justify-center py-4">
                <div class="w-6 h-6 border-t-2 rounded-full animate-spin border-indigo-600"></div>
              </div>
              <div v-else-if="!membersData.length" class="text-xs text-center text-indigo-200">ไม่มีสมาชิก</div>
              <div v-else class="grid grid-cols-1 gap-4">
                <div
                  v-for="(memberData, index) in membersData"
                  :key="index"
                  class="group relative bg-gray-900 bg-opacity-60 rounded-2xl border border-gray-800 overflow-hidden hover:border-indigo-600 transition"
                >
                  <!-- Header: Image + Name + Level -->
                  <div class="p-3 flex items-center">
                    <div class="relative">
                      <img
                        :src="memberData.normalFace || memberData.img || 'https://via.placeholder.com/64?text=No+Img'"
                        :alt="memberData.name"
                        class="w-14 h-14 rounded-lg object-cover border-2 border-indigo-500 bg-gray-800"
                        @error="handleImageError"
                      />
                      <div
                        v-if="memberData.level"
                        class="absolute -bottom-2 left-1/3 transform -translate-x-1/2 px-2 py-0.5 rounded text-xxs bg-indigo-600 text-white shadow"
                      >
                        {{ memberData.level }}
                      </div>
                    </div>
                    <div class="ml-3 flex-1">
                      <div class="flex items-center justify-between">
                        <div class="font-semibold text-gray-100 truncate">
                          {{ memberData.name }}
                          <button @click="openVillagerDetail(memberData.name)" class="ml-1 text-indigo-300 hover:text-indigo-100">
                            <i class="fa-solid fa-circle-info"></i>
                          </button>
                        </div>
                      </div>
                      <div class="flex items-center space-x-1 px-1">
                        <!-- Attack Dice -->
                        <div class="flex items-center space-x-1 px-2 py-0.5 rounded bg-red-900 opacity-80 text-white text-xxs border border-red-600">
                          <i class="fa-solid fa-dice text-red-300 text-xs"></i>
                          <span class="font-mono">{{ getDice(memberData, 'attackDice') }}</span>
                        </div>
                        <!-- Defense Dice -->
                        <div class="flex items-center space-x-1 px-2 py-0.5 rounded bg-blue-900 opacity-80 text-white text-xxs border border-blue-600">
                          <i class="fa-solid fa-dice text-blue-300 text-xs"></i>
                          <span class="font-mono">{{ getDice(memberData, 'defenseDice') }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  <!-- Stats -->
                  <div class="px-3 pb-3 space-y-2">
                    <div class="grid grid-cols-3 gap-1">
                      <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                        sta: {{ getTotalStat(memberData, 'stamina') }}
                      </span>
                      <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                        str: {{ getTotalStat(memberData, 'str') }}
                      </span>
                      <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                        agi: {{ getTotalStat(memberData, 'agi') }}
                      </span>
                      <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                        int: {{ getTotalStat(memberData, 'int') }}
                      </span>
                      <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                        luck: {{ getTotalStat(memberData, 'luck') }}
                      </span>
                      <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                        def: {{ getTotalStat(memberData, 'defense') }}
                      </span>
                    </div>
                  </div>
                  <!-- Highlight -->
                  <div class="absolute inset-x-0 top-0 h-1 bg-indigo-600 opacity-0 group-hover:opacity-100 transition"></div>
                </div>
              </div>
            </div>
          </section>

          <!-- DROPS TAB -->
          <section v-if="activeTab === 'drops'" class="space-y-4 animate-fadeIn">
            <!-- Drop Items -->
            <div class="bg-gray-900 bg-opacity-60 border border-gray-700 rounded-md p-3">
              <div class="flex items-center justify-between mb-2">
                <h3 class="text-xs font-semibold text-indigo-300">ไอเทมดรอปเมื่อชนะ</h3>
                <div class="text-xxs text-gray-400">{{ dropItemsData.length }} ชิ้น</div>
              </div>
              <div v-if="loadingDrops" class="flex justify-center py-4">
                <div class="w-6 h-6 border-t-2 rounded-full animate-spin border-indigo-600"></div>
              </div>
              <div v-else-if="!dropItemsData.length" class="text-xs text-indigo-300 text-center py-6">ไม่มีไอเทมดรอป</div>
              <div v-else class="grid grid-cols-5 sm:grid-cols-10 gap-2">
                <button
                  v-for="(item, index) in sortedDropItems"
                  :key="index"
                  class="group relative cursor-pointer rounded-md overflow-hidden border border-gray-700 hover:border-indigo-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <img
                    v-if="item.img"
                    :src="item.img"
                    :alt="item.name || item.itemName"
                    loading="lazy"
                    class="w-full h-auto object-cover"
                    @error="handleImageError"
                  />
                  <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1 text-left">
                    <div class="text-xxs font-semibold text-indigo-200 truncate">{{ item.name || item.itemName }}</div>
                    <div class="text-xxs text-gray-300">~{{ (item.chance / 100).toFixed(2) }}%</div>
                  </div>
                </button>
              </div>
            </div>

            <!-- First Win Items -->
            <div class="bg-gray-900 bg-opacity-60 border border-gray-700 rounded-md p-3">
              <div class="flex items-center justify-between mb-2">
                <h3 class="text-xs font-semibold text-indigo-300">ไอเทมดรอปครั้งแรก</h3>
                <div class="text-xxs text-gray-400">{{ firstWinItemsData.length }} ชิ้น</div>
              </div>
              <div v-if="loadingFirstDrops" class="flex justify-center py-4">
                <div class="w-6 h-6 border-t-2 rounded-full animate-spin border-indigo-600"></div>
              </div>
              <div v-else-if="!firstWinItemsData.length" class="text-xs text-indigo-300 text-center py-6">ไม่มีไอเทมดรอปครั้งแรก</div>
              <div v-else class="grid grid-cols-5 sm:grid-cols-10 gap-2">
                <button
                  v-for="(item, index) in sortedFirstWinItems"
                  :key="index"
                  class="group relative cursor-pointer rounded-md overflow-hidden border border-gray-700 hover:border-indigo-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <img
                    v-if="item.img"
                    :src="item.img"
                    :alt="item.name || item.itemName"
                    loading="lazy"
                    class="w-full h-auto object-cover"
                    @error="handleImageError"
                  />
                  <div class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1 text-left">
                    <div class="text-xxs font-semibold text-indigo-200 truncate">{{ item.name || item.itemName }}</div>
                    <div v-if="item.quantity" class="text-xxs text-gray-300">x {{ item.quantity }}</div>
                  </div>
                </button>
              </div>
            </div>
          </section>
        </template>
        <template v-else>
          <!-- Villager Selection -->
          <section class="space-y-4 animate-fadeIn">
            <h3 class="text-sm font-semibold text-indigo-300">เลือกชาวบ้านสำหรับท้าสู้ (ขั้นต่ำ {{ minAttackers }} สูงสุด {{ maxAttackers }} คน) - เลือกแล้ว {{ selectedVillagerIds.length }} คน</h3>
            <div v-if="playerVillagers.length > 0" class="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div
                v-for="v in sortedPlayerVillagers"
                :key="v.id"
                class="group relative bg-gray-900 bg-opacity-60 rounded-2xl border border-gray-800 overflow-hidden hover:border-indigo-600 transition"
              >
                <!-- Header: Image + Name + Level -->
                <div class="p-3 flex items-center">
                  <div class="relative">
                    <img
                      :src="v.img || v.template?.normalFace || 'https://via.placeholder.com/64?text=No+Img'"
                      :alt="v.name"
                      class="w-14 h-14 rounded-lg object-cover border-2 border-indigo-500 bg-gray-800"
                      @error="handleImageError"
                    />
                    <div
                      v-if="v.level || v.template?.level"
                      class="absolute -bottom-2 left-1/3 transform -translate-x-1/2 px-2 py-0.5 rounded text-xxs bg-indigo-600 text-white shadow"
                    >
                      {{ v.level ?? v.template?.level }}
                    </div>
                  </div>
                  <div class="ml-3 flex-1">
                    <div class="flex items-center justify-between">
                      <div class="font-semibold text-gray-100 truncate">
                        {{ v.name }}
                        <button @click="openVillagerDetail(v.name)" class="ml-1 text-indigo-300 hover:text-indigo-100">
                          <i class="fa-solid fa-circle-info"></i>
                        </button>
                      </div>
                    </div>
                    <div class="flex items-center space-x-1 px-1">
                      <!-- Attack Dice -->
                      <div class="flex items-center space-x-1 px-2 py-0.5 rounded bg-red-900 opacity-80 text-white text-xxs border border-red-600">
                        <i class="fa-solid fa-dice text-red-300 text-xs"></i>
                        <span class="font-mono">{{ getDice(v, 'attackDice') }}</span>
                      </div>
                      <!-- Defense Dice -->
                      <div class="flex items-center space-x-1 px-2 py-0.5 rounded bg-blue-900 opacity-80 text-white text-xxs border border-blue-600">
                        <i class="fa-solid fa-dice text-blue-300 text-xs"></i>
                        <span class="font-mono">{{ getDice(v, 'defenseDice') }}</span>
                      </div>
                    </div>
                    <div class="text-xs text-gray-400 truncate" v-if="v.template?.note">
                      {{ v.template.note }}
                    </div>
                  </div>
                </div>
                <!-- Stats -->
                <div class="px-3 pb-3 space-y-2">
                  <div class="grid grid-cols-3 gap-1">
                    <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                      sta: {{ getTotalStat(v, 'stamina') }}
                    </span>
                    <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                      str: {{ getTotalStat(v, 'str') }}
                    </span>
                    <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                      agi: {{ getTotalStat(v, 'agi') }}
                    </span>
                    <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                      int: {{ getTotalStat(v, 'int') }}
                    </span>
                    <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                      luck: {{ getTotalStat(v, 'luck') }}
                    </span>
                    <span class="px-2 py-0.5 rounded bg-gray-800 text-gray-300 text-xxs border border-gray-700">
                      def: {{ getTotalStat(v, 'defense') }}
                    </span>
                  </div>
                </div>
                <!-- Selection Button -->
                <div class="px-3 pb-3">
                  <button
                    @click="toggleSelect(v.id)"
                    class="w-full px-2 py-1 text-white rounded text-xs transition"
                    :class="[
                      selectedVillagerIds.includes(v.id) ? 'bg-green-700 hover:bg-green-600' : 
                      isSelectable(v) ? 'bg-indigo-700 hover:bg-indigo-600' : 'bg-gray-700 cursor-not-allowed'
                    ]"
                    :disabled="!isSelectable(v)"
                  >
                    {{ selectedVillagerIds.includes(v.id) ? 'ยกเลิก' : (isSelectable(v) ? 'เลือก' : 'เลือกไม่ได้') }}
                  </button>
                </div>
                <div v-if="!v.canAssign" class="px-3 pb-3 text-xxs text-red-400">
                  ชาวบ้านไม่คุ้นเคยกับสถานที่นี้
                </div>
                <div v-if="isNameAlreadySelected(v.name) && !selectedVillagerIds.includes(v.id)" class="px-3 pb-3 text-xxs text-red-400">
                  ไม่สามารถเลือกชาวบ้านชื่อซ้ำกันได้
                </div>
                <div v-if="selectedVillagerIds.length >= maxAttackers && !selectedVillagerIds.includes(v.id)" class="px-3 pb-3 text-xxs text-red-400">
                  ถึงขีดจำกัดจำนวนชาวบ้านแล้ว
                </div>
                <!-- Highlight -->
                <div class="absolute inset-x-0 top-0 h-1 bg-indigo-600 opacity-0 group-hover:opacity-100 transition"></div>
              </div>
            </div>
            <p v-else class="text-indigo-300 text-xs text-center py-6">คุณไม่มีชาวบ้านสำหรับท้าสู้</p>
          </section>
        </template>
      </main>

      <!-- Footer -->
      <footer class="sticky bottom-0 bg-gray-800 border-t border-gray-700 p-3 flex items-center justify-end gap-2">
        <template v-if="!isSelectingVillagers">
          <!-- แสดงเหตุผลที่ท้าสู้ไม่ได้ -->
          <div v-if="!canAttack" class="mr-auto flex items-center space-x-2 text-red-400 text-xs font-medium bg-red-900 bg-opacity-20 border border-red-700 rounded-md px-3 py-1.5">
            <i class="fa-solid fa-exclamation-triangle"></i>
            <span>{{ attackDisabledReason }}</span>
          </div>
          <button 
            :disabled="!canAttack || attackStarting"
            class="text-xs px-3 py-2 rounded-md transition flex items-center justify-center"
            :class="!canAttack || attackStarting ? 'bg-gray-700 border border-gray-600 text-gray-300 cursor-not-allowed' : 'bg-indigo-700 border border-indigo-600 hover:bg-indigo-600 text-white'"
            @click="startAttack"
          >
            <span v-if="!attackStarting">ท้าสู้กับแก๊งค์</span>
            <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
          </button>
          <button class="text-xs px-3 py-2 rounded-md bg-gray-700 border border-gray-600 hover:bg-gray-600 transition" @click="$emit('cancelModal')">ปิด</button>
        </template>
        <template v-else>
          <button class="text-xs px-3 py-2 rounded-md bg-gray-700 border border-gray-600 hover:bg-gray-600 transition" @click="cancelAttack">ยกเลิก</button>
          <button 
            class="text-xs px-3 py-2 rounded-md bg-indigo-700 border border-indigo-600 hover:bg-indigo-600 transition flex items-center justify-center" 
            :disabled="selectedVillagerIds.length < minAttackers || attacking"
            @click="attackGang"
          >
            <span v-if="!attacking">ท้าสู้</span>
            <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
          </button>
        </template>
      </footer>
    </div>
  </div>
  <DetailVillagerModal
    v-if="showVillagerModal"
    :villager-id="selectedVillager"
    @cancelModal="showVillagerModal = false"
  />
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { projectFirestore, projectFunctions } from '@/firebase/config';
import getUser from '@/composables/getUser';
import { usePlayerStore } from '@/composables/usePlayerStore';
import gameVillagersData from '@/data/gameVillagers.json';
import gameItemsData from '@/data/gameItems.json';
import DetailVillagerModal from './DetailVillagerModal.vue';

export default {
  props: ['gangId'],
  emits: ['cancelModal'],
  components: { DetailVillagerModal },
  setup(props, { emit }) {
    const { user } = getUser();
    const playerStore = usePlayerStore();
    const gangData = ref({});
    const membersData = ref([]);
    const dropItemsData = ref([]);
    const firstWinItemsData = ref([]);
    const isLoading = ref(true);
    const activeTab = ref('info');
    const loadingMembers = ref(false);
    const loadingDrops = ref(false);
    const loadingFirstDrops = ref(false);
    const showVillagerModal = ref(false);
    const selectedVillager = ref(null);
    const imgBroken = ref(false);

    // Selection mode
    const isSelectingVillagers = ref(false);
    const selectedVillagerIds = ref([]);
    const playerVillagers = ref([]);
    const templateCache = ref({});
    const attackStarting = ref(false);
    const attacking = ref(false);
    const maxAttackers = 5;
    const minAttackers = computed(() => gangData.value.minVillagersToAttack || 1);

    // NEW: ตรวจสอบ prerequisites จาก characterPrivate
    const characterPrivate = ref(null);
    const wonGangs = ref([]); // array ชื่อแก๊งค์ที่ชนะ
    const missingPrereqs = ref([]);

    const tabs = ref([
      { id: 'info', name: 'ข้อมูลพื้นฐาน', icon: 'fas fa-info-circle' },
      { id: 'members', name: 'สมาชิก', icon: 'fas fa-people-group' },
      { id: 'drops', name: 'ดรอปไอเทม', icon: 'fas fa-sack-dollar' }
    ]);
    const filteredTabs = computed(() => tabs.value);

    const fetchGang = async () => {
      const doc = await projectFirestore.collection('gameGanks').doc(props.gangId).get();
      gangData.value = doc.data();
      isLoading.value = false;
      await fetchMembers();
      await fetchDropItems();
      await fetchFirstWinItems();
      await checkPrerequisites(); // ตรวจสอบ prerequisites
    };

    const fetchMembers = async () => {
      loadingMembers.value = true;
      membersData.value = gangData.value.members
        .map((name) => {
          const data = gameVillagersData[name];
          return data ? { name, ...data } : null;
        })
        .filter(Boolean);
      loadingMembers.value = false;
    };

    const fetchDropItems = async () => {
      loadingDrops.value = true;
      dropItemsData.value = gangData.value.dropItems.map((drop) => {
        const data = gameItemsData[drop.itemName];
        return data ? { ...drop, ...data } : drop;
      });
      loadingDrops.value = false;
    };

    const fetchFirstWinItems = async () => {
      loadingFirstDrops.value = true;
      firstWinItemsData.value = gangData.value.firstWinItems.map((firstDrop) => {
        const data = gameItemsData[firstDrop.itemName];
        return data ? { ...firstDrop, ...data } : firstDrop;
      });
      loadingFirstDrops.value = false;
    };

    // ตรวจสอบ prerequisites จาก characterPrivate.wonGangs
    const checkPrerequisites = () => {
      wonGangs.value = playerStore.characterPrivateData?.wonGangs || [];

      const prereqs = gangData.value.prerequisites || [];
      if (prereqs.length === 0) {
        missingPrereqs.value = [];
        return;
      }

      const missing = prereqs.filter(name => !wonGangs.value.includes(name.trim()));
      missingPrereqs.value = missing;
    };

    const canAttack = computed(() => missingPrereqs.value.length === 0);

    const attackDisabledReason = computed(() => {
      if (missingPrereqs.value.length > 0) {
        return `ต้องชนะแก๊งค์ก่อน: ${missingPrereqs.value.join(', ')}`;
      }
      return '';
    });

    const openVillagerDetail = (name) => {
      selectedVillager.value = name;
      showVillagerModal.value = true;
    };

    const handleImageError = e => { e.target.src = 'https://via.placeholder.com/64'; };

    const getDice = (v, key) => v[key] || v.template?.[key] || 0;

    const getTotalStat = (v, key) => {
      const base = v.stats?.[key] || v.template?.stats?.[key] || 0;
      const upgrade = v.upgradeStats?.[key] || 0;
      return base + upgrade;
    };

    const sortedDropItems = computed(() => [...dropItemsData.value].sort((a, b) => b.chance - a.chance));

    const sortedFirstWinItems = computed(() => [...firstWinItemsData.value]);

    // Selection logic
    const fetchTemplate = async (name) => {
      if (templateCache.value[name]) return templateCache.value[name];
      const snap = await projectFirestore.collection('gameVillagers').doc(name).get();
      if (snap.exists) {
        templateCache.value[name] = snap.data();
        return templateCache.value[name];
      }
      return null;
    };

    const startAttack = async () => {
      if (!canAttack.value) return;
      attackStarting.value = true;
      isSelectingVillagers.value = true;
      selectedVillagerIds.value = [];
      playerVillagers.value = [];
      try {
        const campSnap = await projectFirestore.collection('camps').doc(user.value.uid).get();
        if (!campSnap.exists) return;
        const villagerIds = campSnap.data().villagerIds || [];
        const villagersData = [];
        for (const id of villagerIds) {
          const vSnap = await projectFirestore.collection('villagers').doc(id).get();
          if (vSnap.exists) {
            villagersData.push({ id: vSnap.id, ...vSnap.data() });
          }
        }

        const uniqueNames = [...new Set(villagersData.map(v => v.name).filter(Boolean))];
        await Promise.all(uniqueNames.map(fetchTemplate));
        const gangLocation = gangData.value.location;
        let mappedVillagers = villagersData.map(v => {
          const template = templateCache.value[v.name] || null;
          const locs = template?.locations || [];
          const canAssign = locs.length === 0 || locs.includes(gangLocation);
          return { ...v, template, canAssign };
        });

        // Sort villagers: by level desc, then by attackDice desc
        mappedVillagers.sort((a, b) => {
          const levelA = a.level ?? a.template?.level ?? 0
          const levelB = b.level ?? b.template?.level ?? 0
          if (levelA !== levelB) return levelB - levelA
          const attackA = getDice(a, 'attackDice')
          const attackB = getDice(b, 'attackDice')
          return attackB - attackA
        });

        playerVillagers.value = mappedVillagers;
      } catch (e) {
        console.error('Error fetching villagers for seize:', e);
      } finally {
        attackStarting.value = false;
      }
    };

    const cancelAttack = () => {
      isSelectingVillagers.value = false;
      selectedVillagerIds.value = [];
      playerVillagers.value = [];
    };

    const toggleSelect = (vid) => {
      if (selectedVillagerIds.value.includes(vid)) {
        selectedVillagerIds.value = selectedVillagerIds.value.filter(id => id !== vid)
      } else if (selectedVillagerIds.value.length < maxAttackers) {
        selectedVillagerIds.value.push(vid)
      }
    }

    const isNameAlreadySelected = (name) => {
      return selectedVillagerIds.value.some(id => {
        const villager = playerVillagers.value.find(v => v.id === id)
        return villager && villager.name === name
      })
    }

    const isSelectable = (v) => {
      const selected = selectedVillagerIds.value.includes(v.id)
      if (selected) return true
      const nameSelected = isNameAlreadySelected(v.name)
      return v.canAssign && !nameSelected && selectedVillagerIds.value.length < maxAttackers
    }

    const sortedPlayerVillagers = computed(() => {
      const selectable = playerVillagers.value.filter(v => isSelectable(v))
      const nonSelectable = playerVillagers.value.filter(v => !isSelectable(v))
      const sortFn = (a, b) => {
        const levelA = a.level ?? a.template?.level ?? 0
        const levelB = b.level ?? b.template?.level ?? 0
        if (levelA !== levelB) return levelB - levelA
        const attackA = getDice(a, 'attackDice')
        const attackB = getDice(b, 'attackDice')
        return attackB - attackA
      }
      return [...selectable.sort(sortFn), ...nonSelectable.sort(sortFn)]
    })

    const attackGang = async () => {
      attacking.value = true
      try {
        const { data } = await projectFunctions.httpsCallable('gangActions')({
          action: 'attackGang',
          gangId: props.gangId,
          villagerIds: selectedVillagerIds.value
        })
        if (data.success) {
          console.log(data.message)
          cancelAttack()
          emit('cancelModal')
        } else {
          console.error(data.error)
        }
      } catch (e) {
        console.error('Error attacking gang:', e)
      } finally {
        attacking.value = false
      }
    }

    onMounted(async () => { await fetchGang() })
    watch(gangData, checkPrerequisites)

    return {
      gangData, membersData, dropItemsData, firstWinItemsData, isLoading, activeTab, loadingMembers, loadingDrops, loadingFirstDrops, handleImageError, showVillagerModal, selectedVillager, openVillagerDetail,
      tabs, filteredTabs, imgBroken, getDice, getTotalStat, sortedDropItems, sortedFirstWinItems,
      isSelectingVillagers, selectedVillagerIds, playerVillagers, startAttack, cancelAttack, toggleSelect, attackGang,
      isNameAlreadySelected, isSelectable, sortedPlayerVillagers, minAttackers, maxAttackers, attackStarting, attacking,
      canAttack, attackDisabledReason
    };
  }
};
</script>

<style scoped>
/* Custom Scrollbar */
.scrollbar-thin::-webkit-scrollbar { width: 5px; }
.scrollbar-thin::-webkit-scrollbar-track { background: #2d3748; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb { background: #4a5568; border-radius: 10px; }
.scrollbar-thin::-webkit-scrollbar-thumb:hover { background: #6b7280; }
.scrollbar-thin { scrollbar-width: thin; scrollbar-color: #4a5568 #2d3748; }

/* Animations */
@keyframes modalFade { from { opacity: 0; } to { opacity: 1; } }
.animate-modalFade { animation: modalFade 0.25s ease-out; }

@keyframes scaleIn { from { transform: scale(0.97); opacity: 0; } to { transform: 1; opacity: 1; } }
.animate-scaleIn { animation: scaleIn 0.25s ease-out; }

@keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
.animate-fadeIn { animation: fadeIn 0.25s ease-out; }

@keyframes tabSlide { from { width: 0; } to { width: 100%; } }
.animate-tabSlide { animation: tabSlide 0.2s ease-out; }

/* Utility tweaks */
.text-xxs { font-size: 0.68rem; line-height: 1rem; }

/* Fade transition */
.fade-enter-active, .fade-leave-active { transition: opacity 0.18s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>